import React from 'react';
import StickerPreview from './StickerPreview';

const PrintableStickerPreview = ({
  layout = 'classic',
  data = {},
  showQR = true,
  qrPosition = 'top', // 'top', 'bottom', 'left', 'right'
  className = '',
  size = 'normal',
  customWidth = null,
  customHeight = null,
  showPaper = true,
  paperSize = 'letter', // 'letter', 'a4', 'label-sheet'
  nameFontSize = null,
  positionFontSize = null,
  orgFontSize = null,
  lineHeight = 1.2,
  logoUrl = null,
  logoSize = 40,
  showLogo = false,
  autoScaleFont = true,
  nameFontWeight = 'bold',
  positionFontWeight = 'semibold',
  orgFontWeight = 'semibold',
  showPosition = true,
  showOrganization = true
}) => {
  // Real-world sticker dimensions in inches (converted to pixels at 96 DPI)
  const stickerSizes = {
    tiny: { width: 96, height: 96 }, // 1" x 1" square sticker
    small: { width: 144, height: 96 }, // 1.5" x 1" rectangular sticker
    '40x20mm': { width: 151, height: 76 }, // 40mm × 20mm sticker
    '40x30mm': { width: 151, height: 113 }, // 40mm × 30mm sticker
    normal: { width: 192, height: 144 }, // 2" x 1.5" standard name tag sticker
    medium: { width: 240, height: 144 }, // 2.5" x 1.5" larger name tag
    '60x40mm': { width: 226, height: 151 }, // 60mm × 40mm sticker
    large: { width: 288, height: 192 }, // 3" x 2" large event sticker
    '80x50mm': { width: 302, height: 189 }, // 80mm × 50mm sticker
  };

  // Paper dimensions in pixels at 96 DPI
  const paperSizes = {
    letter: { width: 816, height: 1056 }, // 8.5" x 11"
    a4: { width: 794, height: 1123 }, // 210mm x 297mm
    'label-sheet': { width: 816, height: 1056 }, // Standard label sheet
  };

  // Get actual sticker dimensions
  const actualStickerSize = customWidth && customHeight
    ? { width: customWidth, height: customHeight }
    : stickerSizes[size];

  const paperDimensions = paperSizes[paperSize];

  // Calculate scale for preview (show paper at manageable size)
  const maxPreviewWidth = 400; // Maximum preview width
  const maxPreviewHeight = 500; // Maximum preview height

  // Calculate scale to fit within preview bounds while maintaining aspect ratio
  const paperScale = Math.min(
    maxPreviewWidth / paperDimensions.width,
    maxPreviewHeight / paperDimensions.height,
    0.5 // Maximum 50% scale
  );

  const scaledPaper = {
    width: paperDimensions.width * paperScale,
    height: paperDimensions.height * paperScale
  };

  // Note: We use CSS transform scale instead of pre-calculated dimensions
  // to maintain proper aspect ratios

  if (!showPaper) {
    // Just show the sticker at actual print size
    return (
      <div className="flex flex-col items-center space-y-2">
        <div className="text-xs text-gray-500 font-medium">
          Actual Print Size: {(actualStickerSize.width / 96).toFixed(1)}" × {(actualStickerSize.height / 96).toFixed(1)}"
        </div>
        <StickerPreview
          layout={layout}
          data={data}
          showQR={showQR}
          qrPosition={qrPosition}
          className={className}
          size={size}
          customWidth={actualStickerSize.width}
          customHeight={actualStickerSize.height}
          nameFontSize={nameFontSize}
          positionFontSize={positionFontSize}
          orgFontSize={orgFontSize}
          lineHeight={lineHeight}
          logoUrl={logoUrl}
          logoSize={logoSize}
          showLogo={showLogo}
          autoScaleFont={autoScaleFont}
          nameFontWeight={nameFontWeight}
          positionFontWeight={positionFontWeight}
          orgFontWeight={orgFontWeight}
          showPosition={showPosition}
          showOrganization={showOrganization}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-4">
      {/* Paper size info */}
      <div className="text-center">
        <div className="text-sm font-medium text-gray-700">
          Print Preview - {paperSize.toUpperCase()} Paper
        </div>
        <div className="text-xs text-gray-500">
          Sticker: {(actualStickerSize.width / 96).toFixed(1)}" × {(actualStickerSize.height / 96).toFixed(1)}" |
          Paper: {(paperDimensions.width / 96).toFixed(1)}" × {(paperDimensions.height / 96).toFixed(1)}"
        </div>
        <div className="text-xs text-gray-400 mt-1">
          Preview shown at {Math.round(paperScale * 100)}% scale
        </div>
      </div>

      {/* Paper with centered sticker */}
      <div
        className="relative bg-white border-2 border-gray-300 shadow-lg preview-container"
        style={{
          width: `${scaledPaper.width}px`,
          height: `${scaledPaper.height}px`,
        }}
      >
        {/* Paper texture/grid lines */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full"
            style={{
              backgroundImage: `
                   linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
                 `,
              backgroundSize: '20px 20px'
            }}>
          </div>
        </div>

        {/* Centered sticker - positioned absolutely in center */}
        <div
          className="absolute"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div
            style={{
              width: `${actualStickerSize.width}px`,
              height: `${actualStickerSize.height}px`,
              transform: `scale(${paperScale})`,
              transformOrigin: 'center center'
            }}
          >
            <StickerPreview
              layout={layout}
              data={data}
              showQR={showQR}
              qrPosition={qrPosition}
              className="shadow-md"
              size={size}
              customWidth={actualStickerSize.width}
              customHeight={actualStickerSize.height}
              nameFontSize={nameFontSize}
              positionFontSize={positionFontSize}
              orgFontSize={orgFontSize}
              lineHeight={lineHeight}
              logoUrl={logoUrl}
              logoSize={logoSize}
              showLogo={showLogo}
              autoScaleFont={autoScaleFont}
              nameFontWeight={nameFontWeight}
              positionFontWeight={positionFontWeight}
              orgFontWeight={orgFontWeight}
              showPosition={showPosition}
              showOrganization={showOrganization}
            />
          </div>
        </div>

        {/* Corner markers to show paper edges */}
        <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-gray-400"></div>
        <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-gray-400"></div>
        <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-gray-400"></div>
        <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-gray-400"></div>
      </div>

      {/* Print instructions */}
      <div className="text-center max-w-md">
        <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-lg">
          <div className="font-medium text-blue-800 mb-1">📄 Print Instructions</div>
          <div className="text-blue-700">
            • Use {paperSize.toUpperCase()} size paper ({(paperDimensions.width / 96).toFixed(1)}" × {(paperDimensions.height / 96).toFixed(1)}")
            <br />
            • Sticker will be centered on the page
            <br />
            • Print at 100% scale (no fit-to-page)
            <br />
            • Cut around sticker edges after printing
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintableStickerPreview;
